import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { TermsConsentProvider, useTermsConsent } from './contexts/TermsConsentContext';
import ConsentPopup from './components/ConsentPopup';
import GoogleAnalytics from './components/GoogleAnalytics';
import Search from './components/Search'; 
import FAQ from './components/FAQ'; 
import Contact from './components/Contact'; 
import Citations from './components/Citations'; 

// Main App layout with consent popup check
const AppLayout = () => {
  const { showConsentPopup, handleAccept, handleDecline } = useTermsConsent();
  
  useEffect(() => {
    document.title = "DFCI MM Data Access Portal";
  }, []);
  
  return (
    <>
      {showConsentPopup && (
        <ConsentPopup 
          onAccept={handleAccept} 
          onDecline={handleDecline} 
        />
      )}
      
      <div className="app-container">
        <Header />
        <main className="main-content">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/search" element={<Search />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/citations" element={<Citations />} />
            <Route path="/contact" element={<Contact />} />
          </Routes>
        </main>
        <Footer />
      </div>
    </>
  );
};



const App = () => {
  return (
    <Router>
      <GoogleAnalytics />
      <TermsConsentProvider>
        <AppLayout />
      </TermsConsentProvider>
    </Router>
  );
};

// Header Component
const Header = () => {
  return (
    <header className="header">
      <div className="website-name">DFCI/Riney Data Access Portal</div>
      <div className="logo-container">
        <img src="img/genomics-logo.svg" alt="DFCI MM Logo" className="logo" />
      </div>
      <nav className="main-menu">
        <ul>
          <li><Link to="/">Home</Link></li>
          <li><Link to="/search">Search</Link></li>
          <li><Link to="/faq">FAQ</Link></li>
          <li><Link to="/citations">Citations</Link></li>
          <li><Link to="/contact">Contact</Link></li>
        </ul>
      </nav>
    </header>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer className="footer">
      <div className="copyright">
        Copyright © 2025 DFCI/Riney Data Access Portal
      </div>
      <nav className="footer-menu">
        <ul>
          <li><Link to="/">Home</Link></li>
          <li><Link to="/search">Search</Link></li>
          <li><Link to="/faq">FAQ</Link></li>
          <li><Link to="/citations">Citations</Link></li>
          <li><Link to="/contact">Contact</Link></li>
        </ul>
      </nav>
    </footer>
  );
};

// Image Slider Component
const ImageSlider = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const slides = [
    {
      image: "/slider-image-1.png",
      caption: "IFM 2009 Newly Diagnosed MM RNAseq Results"
    },
    {
      image: "/slider-image-2.png",
      caption: "IFM 2009 Newly Diagnosed MM WGS Results"
    },
    {
      image: "/slider-image-3.png",
      caption: "DFCI/IFM High Risk Cohort"
    },
    {
      image: "/slider-image-3.png",
      caption: "DFCI/IFM Precursor Cohort"
    },
    {
      image: "/slider-image-3.png",
      caption: "DFCI/IFM Low Risk Cohort"
    }
  ];
  
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
    }, 5000);
    
    return () => clearInterval(interval);
  }, [slides.length]);
  
  const goToSlide = (index) => {
    setCurrentSlide(index);
  };
  
  const goToPrevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  };
  
  const goToNextSlide = () => {
    setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
  };
  
  return (
    <div className="slider-container">
      <div className="slider">
        {slides.map((slide, index) => (
          <div 
            key={index}
            className={`slide ${index === currentSlide ? 'active' : ''}`}
            style={{ backgroundImage: `url(${slide.image})` }}
          >
            <div className="slide-caption">{slide.caption}</div>
          </div>
        ))}
        
        <button className="slider-arrow prev-arrow" onClick={goToPrevSlide}>
          &lt;
        </button>
        <button className="slider-arrow next-arrow" onClick={goToNextSlide}>
          &gt;
        </button>
      </div>
      
      <div className="slider-navigation">
        {slides.map((_, index) => (
          <button 
            key={index} 
            className={`slider-dot ${index === currentSlide ? 'active' : ''}`}
            onClick={() => goToSlide(index)}
          />
        ))}
      </div>
    </div>
  );
};

const Home = () => {
  return (
    <div className="home-page">
      <ImageSlider />
    </div>
  );
};

// Placeholder Pages


export default App;